import { CommonModule } from '@angular/common';
import {
  Component,
  ChangeDetectionStrategy,
  Output,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  forwardRef,
  OnChanges,
  SimpleChanges,
  signal,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { LetDirective } from '@directives/app-let.directive';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Station } from '@models/responses/station-response.model';
import { NullableString } from '@models/app.models';
import { Observable, Subject, debounce, debounceTime, takeUntil } from 'rxjs';
import { StationNamePipe } from '@pipes/station-name/station-name.pipe';
import { GrowthBookService } from '@services/growthbook/growthbook.service';
import { GrowthBookFeatureKeys } from '@models/growthbook.models';
import { Store } from '@ngrx/store';
import { CULTURE_ROUTE_PARAM, CultureKeyType } from '@constants/app.constants';
import { selectRouteParam } from '@selectors/router.selector';
import {
  SafeHtmlPipe,
  SanitizerValueType,
} from '@pipes/safe-html/safe-html.pipe';
import { RudderStackService } from '@services/rudderstack/rudderstack.service';
import { FeatureFlagService } from '@services/feature-flag/feature-flag.service';
import { FeatureFlagsEnum } from '@models/environment.type';
import { ContinentStationZonePipe } from '@pipes/continent-station-zone/continent-station-zone.pipe';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { InputComponent } from '@components/controls/input/input.component';
import { InputDirective } from '@directives/input/input.directive';
import { SearchWidgetCopy } from '@models/cms/search-widget.model';

@Component({
  selector: 'app-station-picker-modal',
  standalone: true,
  templateUrl: './station-picker-modal.component.html',
  styleUrls: ['./station-picker-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(
        /* istanbul ignore next */
        () => StationPickerModalComponent,
      ),
      multi: true,
    },
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    CollapseModule,
    LetDirective,
    StationNamePipe,
    SafeHtmlPipe,
    ContinentStationZonePipe,
    InputComponent,
    InputDirective,
  ],
})
export class StationPickerModalComponent
  implements OnInit, OnChanges, OnDestroy, ControlValueAccessor
{
  @Input() stations: Station[] | null = null;
  @Input() dohopFeaturedStations: string[] | null = null;
  @Input() showDohop: boolean = true;
  @Input() isOrigin = false;
  @Input() isDestination = false;
  @Input() showInput = false;
  @Input() hideHelpInfo = false;
  @Input() customTitle: string | undefined;
  @Input() originStationCode: string | null = null;
  @Input() shouldUseMobileStyling: Observable<boolean>;
  @Input() copy: SearchWidgetCopy;
  @Output() stationChange = new EventEmitter<Station>();

  readonly SanitizerValueType = SanitizerValueType;

  value: unknown;
  changed: (value: string) => void;
  touched: () => void;
  searchStationCtrl: FormControl;
  onDestroy$ = new Subject<void>();
  norseStations = signal([] as Station[]);
  dohopStations = signal([] as Station[]);
  collapse = {
    dohop: false,
    norse: false,
  };

  stationSearchCtrl = new FormControl<string | null>(null);

  readonly gbf = GrowthBookFeatureKeys;
  readonly ffe = FeatureFlagsEnum;

  culture$ = this.store.select(
    selectRouteParam(CULTURE_ROUTE_PARAM),
  ) as Observable<CultureKeyType | undefined>;
  dohopFooterContent = `<p>Can’t find the airport you’re looking for?<br /><a href="https://connections.flynorse.com/?utm_source=norse_picker&rid=${this.rudderStackService.getAnonymousId()}" target="_blank">Book a connecting flight with us and our partners</a></p>`;

  constructor(
    private store: Store,
    public bsModalRef: BsModalRef,
    public gbs: GrowthBookService,
    private changeDetector: ChangeDetectorRef,
    private rudderStackService: RudderStackService,
    public ffs: FeatureFlagService,
  ) {
    this.searchStationCtrl = new FormControl('');
  }

  ngOnInit(): void {
    this.updateStations();
    this.searchStationCtrl?.valueChanges
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.updateStations();
      });

    this.gbs.onChange$.pipe(takeUntil(this.onDestroy$)).subscribe(() => {
      this.changeDetector.detectChanges();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.stations) {
      this.updateStations();
    }
  }

  private setNorseStations(filter?: string | null): Station[] {
    return (
      this.stations
        ?.filter((s) => this.isOrigin ? s.isNorse : true)
        .filter((s) => this.isDestination ? !s.validMarkets?.find(m => m.stationCode === this.originStationCode)?.isDohop : true)
        .filter((s) =>
          filter
            ? `${s.fullName} ${s.stationCode}`
                .toLowerCase()
                .includes(filter.toLowerCase() ?? '~')
            : true,
        ) ?? []
    );
  }

  private setDohopStations(filter?: string | null): Station[] {
    return (
      this.stations
        ?.filter((s) => this.isOrigin ? !s.isNorse : true)
        .filter((s) => this.isDestination ? s.validMarkets?.find(m => m.stationCode === this.originStationCode)?.isDohop : true)
        .filter((s) =>
          filter
            ? `${s.fullName} ${s.stationCode}`
                .toLowerCase()
                .includes(filter.toLowerCase() ?? '~')
            : !!this.dohopFeaturedStations?.length ? this.dohopFeaturedStations.includes(s.stationCode) : true,
        ) ?? []
    );
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
  }

  onStationSelect(station: Station): void {
    this.stationChange.emit(station);
  }

  getMacOverride(code: NullableString): string {
    if (code == 'JM1' || code == 'FL1' || code == 'SF1') {
      return 'ALL';
    }
    return code ?? '';
  }

  /* istanbul ignore next */
  writeValue(value: unknown): void {
    this.value = value;
  }

  /* istanbul ignore next */
  registerOnChange(fn: () => void): void {
    this.changed = fn;
  }

  /* istanbul ignore next */
  registerOnTouched(fn: () => void): void {
    this.touched = fn;
  }

  updateStations(): void {
    const query = this.searchStationCtrl?.value as NullableString;

    this.norseStations.set(this.setNorseStations(query));
    if (
      this.ffs.isFeatureActive(FeatureFlagsEnum.CONNECTIONS) &&
        this.gbs.growthBook.isOn(this.gbf.FEAT_US366_DOHOP_V2_TOP_CONNECTIONS) &&
      ['b', 'c'].includes(this.gbs.growthBook.getFeatureValue(
        this.gbf.FEAT_US366_DOHOP_V2_TOP_CONNECTIONS,
        ''
      ))
    ) {
      this.dohopStations.set(this.setDohopStations(query));
    }

    if (
      this.gbs.growthBook.isOn(this.gbf.FEAT_US366_DOHOP_V2_TOP_CONNECTIONS) &&
      this.gbs.growthBook.getFeatureValue(this.gbf.FEAT_US366_DOHOP_V2_TOP_CONNECTIONS, '') === 'b' && this.norseStations().length
    ) {
      this.collapse.dohop = true;
    } else if (this.isDestination && !this.norseStations().length) {
      this.collapse.dohop = false;
    }

    this.changeDetector.detectChanges();
  }
}
